/**
 * Client-side feature flag gating aligned with API-resolved `configs.featureFlags`.
 *
 * GA flags use opt-out semantics (missing = enabled for backwards compatibility).
 * Beta / preview flags use opt-in semantics (must be explicitly `true`).
 */

/** @type {'opt_out'|'opt_in'} */
export const FEATURE_GATE_MODE = Object.freeze({
  OPT_OUT: "opt_out",
  OPT_IN: "opt_in",
});

/** Opt-in gate for the jobs v2 workspace. Off until a tenant turns it on. */
export const JOBS_V2_FEATURE_KEY = "jobs.v2.enabled";

/**
 * Minimal beta package — the only pilot knobs for current release.
 * Keep these `lifecycle: beta` + `defaultEnabled: false` in the API registry.
 * Do not add finer splits unless a pilot needs partial enablement.
 *
 * @type {readonly string[]}
 */
export const MINIMAL_BETA_FEATURE_KEYS = Object.freeze([
  "manifests.v2.enabled",
  "manifests.v2.compliance.enabled",
  "dispatch.planning.enabled",
  "dispatch.buildTrip.enabled",
  "jobs.legs.fleetEquipment.enabled",
  JOBS_V2_FEATURE_KEY,
  "integrations.motive.enabled",
  "assistant.v2.enabled",
]);

/**
 * Beta / preview keys that require explicit `true` in resolved tenant flags.
 * @type {Set<string>}
 */
const OPT_IN_FEATURE_KEYS = new Set(MINIMAL_BETA_FEATURE_KEYS);

/**
 * @param {string} featureKey
 * @returns {'opt_out'|'opt_in'}
 */
export function getFeatureGateMode(featureKey) {
  if (OPT_IN_FEATURE_KEYS.has(featureKey)) {
    return FEATURE_GATE_MODE.OPT_IN;
  }
  return FEATURE_GATE_MODE.OPT_OUT;
}

/**
 * Whether a feature is enabled for the tenant.
 *
 * @param {Record<string, boolean>|undefined|null} featureFlags
 * @param {string} [featureKey]
 * @param {{ optIn?: boolean, mode?: 'opt_out'|'opt_in' }} [options]
 * @returns {boolean}
 */
export function isFeatureEnabled(featureFlags, featureKey, options = {}) {
  if (!featureKey) return true;

  const mode =
    options.mode ||
    (options.optIn ? FEATURE_GATE_MODE.OPT_IN : getFeatureGateMode(featureKey));
  const value = featureFlags?.[featureKey];

  if (mode === FEATURE_GATE_MODE.OPT_IN) {
    return value === true;
  }

  if (value === undefined) return true;
  return Boolean(value);
}

/**
 * Opt-in to console logging of resolved feature flags after config load.
 * Run in browser devtools: `localStorage.setItem('debugFeatureFlags', 'true'); location.reload();`
 *
 * @param {Record<string, boolean>|undefined|null} featureFlags
 * @param {{ parentCompany?: string }} [context]
 */
export function debugLogFeatureFlags(featureFlags, context = {}) {
  try {
    if (
      typeof window === "undefined" ||
      window?.localStorage?.getItem("debugFeatureFlags") !== "true"
    ) {
      return;
    }
    // eslint-disable-next-line no-console
    console.groupCollapsed("[Config][debugFeatureFlags] Resolved feature flags");
    // eslint-disable-next-line no-console
    console.log("parentCompany:", context.parentCompany);
    if (typeof context === "string") {
      // eslint-disable-next-line no-console
      console.log("context:", context);
    }
    // eslint-disable-next-line no-console
    console.table(featureFlags || {});
    // eslint-disable-next-line no-console
    console.groupEnd();
  } catch {
    // no-op
  }
}

/** @deprecated Use {@link debugLogFeatureFlags} */
export const debugFeatureFlags = debugLogFeatureFlags;

/**
 * Evaluate a feature against the full config object from `useConfig`.
 *
 * @param {object} [configs]
 * @param {string} [featureKey]
 * @param {{ optIn?: boolean, mode?: 'opt_out'|'opt_in' }} [options]
 * @returns {boolean}
 */
export function isFeatureEnabledFromConfig(configs, featureKey, options = {}) {
  return isFeatureEnabled(configs?.featureFlags, featureKey, options);
}
